import React, {useEffect, useState} from 'react';
import {Dropdown} from 'primereact/dropdown';
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import {getQueueDirectoriesQueueAudioType, getAllQueues, getQueueIVRType, buildIvrDropdownOptions} from "../../../redux/api/apiQueue";
import {classNames} from "primereact/utils";
import {Button} from "primereact/button";
import {setChangedCompaniesData, setCompanyField, setEditType} from "../../../redux/actions/actions";
import {checkPermissions} from "../../../redux/permissions/permissionsUtils";
import {displayAudioGroupId, getAudioById, isValidAudioGroupId} from "../../../redux/api/apiAudio";
import AudioDialog from "../../Settings/AudioList/AudioDialog";
import {MultiSelect} from "primereact/multiselect";
import {InputSwitch} from "primereact/inputswitch";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";

const CampaignDialog_IVM_ACD = () => {
    const dispatch = useAppDispatch()
    const companiesData = useAppSelector(state => state.CompaniesListReducer.companiesData);
    const queuesDirectoryQueueAudioType = useAppSelector(state => state.QueueListReducer.queuesDirectoryQueueAudioType);
    const archiveTableToggle = useAppSelector(state => state.CompaniesListReducer.archiveTableToggle);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const errorFields = useAppSelector(state => state.CompaniesListReducer.errorFields);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const dialerPermissions = checkPermissions(rolesVision, 'Audio');
    const detailsLoading = useAppSelector(state => state.AudioListReducer.detailsLoading);
    const audioDialogVisible = useAppSelector(state => state.AudioListReducer.audioDialogVisible);
    const [audioId, setAudioId] = useState<any>(null)
    const statusDone = companiesData?.campaign?.statusId === 6;
    const audioIsBlock = companiesData?.permission?.audioIsBlock;
    const ivrIsBlock = companiesData?.permission?.ivrIsBlock;
    const campaignDetailsLoading = useAppSelector(state => state.CompaniesListReducer.detailsLoading);
    const disabledInputClass = classNames('w-full', {'opacity-100': archiveTableToggle && !statusDone && editType});
    const IVRConfiguration = useAppSelector(state => state.QueueListReducer.IVRConfiguration);
    const selectedIvrId = companiesData?.campaignACD?.ivrConfigurationId;
    const ivrDropdownOptions = buildIvrDropdownOptions(
        IVRConfiguration,
        selectedIvrId,
        companiesData?.campaignACD?.ivrConfigurationName
    );
    // @ts-ignore
    const allAudioGroup = useAppSelector(state => state.QueueListReducer.allAudioGroup);
    const [selectedIvmGroupId, setSelectedIvmGroupId] = useState<number | string | null>(null);
    const {
        visible: hasAudioVisiblePermission,
        update: hasAudioUpdatePermission,
    } = dialerPermissions;

    const sameAudioId = (a: any, b: any) =>
        a != null && b != null && String(a) === String(b);

    const findAccessibleAudio = (audioId: any) =>
        queuesDirectoryQueueAudioType?.find((audio: any) => sameAudioId(audio.audioId, audioId))
        || allAudioGroup?.find((audio: any) => sameAudioId(audio.audioId, audioId));

    const canOpenAudioDetails = (audioId: any) => {
        if (campaignDetailsLoading || audioIsBlock || !hasAudioVisiblePermission || audioId == null) {
            return false;
        }
        const audio = findAccessibleAudio(audioId);
        return isValidAudioGroupId(audio?.groupId);
    };

    const audioDetailsIcon = archiveTableToggle && hasAudioUpdatePermission ? 'pi pi-pencil' : 'pi pi-eye';

    useEffect(() => {
        if (audioIsBlock) {
            return;
        }
        const firstAudio = companiesData?.campaignACD?.ivmAudios?.[0];
        const found:any = allAudioGroup?.find((audio:any)=> sameAudioId(audio.audioId, firstAudio?.audioId));
        const groupId = displayAudioGroupId(
            found?.groupId,
            firstAudio?.audioId,
            firstAudio?.groupName || found?.groupName,
            'campaign'
        );
        if (groupId != null) {
            setSelectedIvmGroupId(groupId);
        }
    }, [audioIsBlock, allAudioGroup]);

    useEffect(() => {
        if (campaignDetailsLoading) {
            return;
        }
        if (!ivrIsBlock) {
            dispatch(getQueueIVRType(jwtToken?.jwtToken));
        }
        if (audioIsBlock) {
            return;
        }
        dispatch(getQueueDirectoriesQueueAudioType(jwtToken?.jwtToken));
    }, [ivrIsBlock, audioIsBlock, campaignDetailsLoading, jwtToken?.jwtToken]);

    useEffect(() => {
        let dictionaryId = 202;
        if (companiesData?.campaignACD?.schedulerStrategy == dictionaryId || companiesData?.campaignACD?.mwtStrategy == dictionaryId || companiesData?.campaignACD?.mwtNoAgentStrategy) {
            dispatch(getAllQueues(jwtToken?.jwtToken));
        }
    }, [companiesData?.campaignACD?.schedulerStrategy, companiesData?.campaignACD?.mwtStrategy, companiesData?.campaignACD?.mwtNoAgentStrategy]);

    const onAudioRowReorder = (e: { dragIndex: number; dropIndex: number }) => {
        let updatedAudios = [...(companiesData?.campaignACD?.ivmAudios || [])];
        const draggedRow = updatedAudios[e.dragIndex];

        updatedAudios.splice(e.dragIndex, 1);
        updatedAudios.splice(e.dropIndex, 0, draggedRow);

        updatedAudios = updatedAudios.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        dispatch(setCompanyField({section: 'campaignACD', fieldName: 'ivmAudios', value: updatedAudios}));
    };

    const audioItemsTable = () => {
        const value = audioIsBlock
            ? [...(companiesData?.campaignACD?.ivmAudios || [])]
                .sort((a: { order: number; }, b: { order: number; }) => a.order - b.order)
            : companiesData?.campaignACD?.ivmAudios
                ?.map((audioObj: any) => {
                    const found = queuesDirectoryQueueAudioType?.find((a: any) => sameAudioId(a.audioId, audioObj.audioId));
                    return found ? {...found, order: audioObj.order} : audioObj;
                })
                ?.filter(Boolean)
                ?.sort((a: { order: number; }, b: { order: number; }) => a.order - b.order);


        const recalculateAudioOrders = (audioList: { audioId: number }[]) => {
            return audioList.map((audio, index) => ({
                ...audio,
                order: index + 1
            }));
        };
        const handleRemoveAudio = (audioIdToRemove: number) => {
            const updated = companiesData?.campaignACD?.ivmAudios?.filter((item: any) => item.audioId !== audioIdToRemove) || [];
            const reordered = recalculateAudioOrders(updated);

            dispatch(setCompanyField({
                section: 'campaignACD',
                fieldName: 'ivmAudios',
                value: reordered
            }));
        };

        return (
            <DataTable
                value={value}
                reorderableRows={!audioIsBlock}
                onRowReorder={audioIsBlock ? undefined : onAudioRowReorder}
                className="w-full"
            >
                {!audioIsBlock && <Column rowReorder style={{width: '3rem'}}/>}
                <Column field="order" style={{width: '30px'}}/>
                <Column field="audioName" style={{width: '90%'}}/>
                {!audioIsBlock && hasAudioVisiblePermission && <Column
                    className="px-0"
                    body={(rowData) => {
                        const accessibleAudio = findAccessibleAudio(rowData.audioId);
                        return canOpenAudioDetails(rowData.audioId) ? (
                        <Button
                            icon={audioDetailsIcon}
                            className="p-button-secondary pr-0"
                            loading={rowData?.groupId === audioId && detailsLoading}
                            onClick={() => {
                                if (!accessibleAudio?.groupId) {
                                    return;
                                }
                                dispatch(getAudioById(accessibleAudio.groupId, jwtToken?.jwtToken, true));
                                setAudioId(accessibleAudio.groupId);
                                dispatch(setEditType(!!hasAudioUpdatePermission));
                            }}
                            disabled={!archiveTableToggle || detailsLoading}
                        />
                        ) : null
                    }}
                />}
                {!audioIsBlock && <Column
                    className="px-0"
                    body={(rowData) => (
                        <Button
                            icon="pi pi-times"
                            className="p-button-secondary px-0 mx-1"
                            onClick={() => {
                                handleRemoveAudio(rowData.audioId);
                            }}
                            disabled={!archiveTableToggle || !editType}
                        />
                    )}
                    style={{width: '3rem'}}
                />}
            </DataTable>
        );
    };

    return (
        <div className="acd-settings grid">
            <div className="p-fluid grid col-12 md:col-12 pl-2">
                <div className="field col-12 md:col-12 m-0">
                    <div className="field-radiobutton">
                        <label htmlFor="name" className="m-0 mr-2">Аудіо повідомлення</label>
                        <InputSwitch className="blueSide"
                            disabled={!archiveTableToggle || statusDone || !editType}
                            // disabled
                            checked={!companiesData?.campaignACD?.isIVMAudio} onChange={(e) => {
                            dispatch(setCompanyField({section: 'campaignACD', fieldName: 'isIVMAudio', value: !e.value}));
                            dispatch(setChangedCompaniesData(true))
                        }}/>
                        <label htmlFor="icon">IVR Config</label>
                    </div>
                </div>
                {!!companiesData?.campaignACD?.isIVMAudio && <div className="p-fluid field col-12 md:col-3 mb-0">
                    <div className="pl-0 pr-1">
                        <label htmlFor="audioSource" className={classNames('block mb-3', {'p-error': errorFields?.ivmAudios})}>Група аудіо*</label>
                        {audioIsBlock ? (
                            <InputText
                                className={classNames(disabledInputClass, {'p-invalid': errorFields?.ivmAudios})}
                                disabled
                                value={companiesData?.campaignACD?.ivmAudios?.[0]?.groupName || ''}
                            />
                        ) : (
                            <Dropdown
                                filter
                                value={selectedIvmGroupId}
                                onChange={(e) => {
                                    setSelectedIvmGroupId(e.value);
                                }}
                                options={Array.from(
                                    new Map([
                                        ...(allAudioGroup || []).map((item: any) => [item.groupId, {
                                            groupId: item.groupId,
                                            groupName: item.groupName
                                        }]),
                                        ...(companiesData?.campaignACD?.ivmAudios || []).map((item: any) => {
                                            const found = findAccessibleAudio(item.audioId);
                                            const groupId = displayAudioGroupId(
                                                found?.groupId,
                                                item.audioId,
                                                item.groupName || found?.groupName,
                                                'campaign'
                                            );
                                            return [groupId, {
                                                groupId,
                                                groupName: item.groupName || found?.groupName || item.audioName,
                                            }];
                                        }),
                                    ].filter((entry: any[]) => entry[0] != null)).values()
                                )}
                                className={classNames('w-full mr-2', {'p-invalid': errorFields?.ivmAudios})}
                                optionLabel="groupName"
                                optionValue="groupId"
                                placeholder="Виберіть групу"
                                disabled={!archiveTableToggle || statusDone || !editType}
                            />
                        )}
                    </div>
                </div>}
                {!companiesData?.campaignACD?.isIVMAudio && <div className="p-fluid field col-12 md:col-4 mb-0">
                    <div className="pl-0 pr-1">
                        <label htmlFor="audioSource" className="block mb-3">IVR Config</label>
                        {ivrIsBlock ? (
                            <InputText
                                className={classNames(disabledInputClass, 'mt-2 mr-2')}
                                disabled
                                value={[
                                    companiesData?.campaignACD?.ivrConfigurationId,
                                    companiesData?.campaignACD?.ivrConfigurationName
                                ].filter((part) => part != null && String(part).trim() !== '').join(' ')}
                            />
                        ) : (
                            <Dropdown
                                id="audioSource"
                                appendTo="self"
                                filter
                                value={selectedIvrId != null && selectedIvrId !== ''
                                    ? (Number.isFinite(Number(selectedIvrId)) ? Number(selectedIvrId) : selectedIvrId)
                                    : null}
                                options={ivrDropdownOptions}
                                optionLabel="label"
                                optionValue="flowId"
                                className="mt-2 mr-2"
                                disabled={!archiveTableToggle || !editType}
                                onChange={(e) => {
                                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'ivrConfigurationId', value: e.value}));
                                }}
                                placeholder="Виберіть IVR конфігурацію"/>
                        )}
                    </div>
                </div>}
                <div className="flex-column field col-12 md:col-4 mb-0">
                    <div>
                        <div className={classNames('mb-3', {'p-error': errorFields?.audioRetries})}>Відтворити разів (мін. 1 раз)*</div>
                    </div>
                    <InputNumber
                        min={0}
                        id="maxWaitTime"
                        value={companiesData?.campaignACD?.audioRetries || null}
                        useGrouping={false}
                        className={classNames('max-w-20rem', {'p-invalid': errorFields?.audioRetries})}
                        disabled={!archiveTableToggle || statusDone || !editType}
                        onChange={(e) => {
                            dispatch(setCompanyField({section: 'campaignACD', fieldName: 'audioRetries', value: e.value}));
                        }}
                        placeholder="0"
                    />
                    {errorFields?.audioRetries && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span
                        className="title p-error text-sm">{errorFields?.audioRetries?.audioRetries}</span></div>}
                </div>
                <div className="p-fluid field col-12 md:col-12 mb-0 p-0"></div>
                {!!companiesData?.campaignACD?.isIVMAudio && <div className="p-fluid field col-12 md:col-6 mb-0">
                    {audioIsBlock ? (
                        <InputText
                            className={classNames(disabledInputClass, {'p-invalid': errorFields?.ivmAudios})}
                            disabled
                            value={(companiesData?.campaignACD?.ivmAudios || [])
                                .map((a: any) => a.audioName)
                                .filter(Boolean)
                                .join(', ')}
                        />
                    ) : (
                        <MultiSelect
                            filter
                            value={companiesData?.campaignACD?.ivmAudios?.map((a: any) => a.audioId) || []}
                            onChange={(e) => {
                                const groupOptions = allAudioGroup?.filter(
                                    (item: { groupId: number | null; }) => item.groupId === selectedIvmGroupId
                                );
                                const selectedGroup = groupOptions?.[0];
                                const updated = e.value?.map((audioId: number, index: number) => {
                                    const audio = groupOptions?.find((a: any) => a.audioId === audioId)
                                        || allAudioGroup?.find((a: any) => a.audioId === audioId);
                                    return {
                                        audioId,
                                        order: index + 1,
                                        audioName: audio?.audioName || '',
                                        groupName: audio?.groupName || selectedGroup?.groupName || '',
                                        groupId: audio?.groupId ?? selectedIvmGroupId,
                                    };
                                });
                                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'ivmAudios', value: updated}));
                            }}
                            className={classNames('w-full', {'p-invalid': errorFields?.ivmAudios})}
                            options={allAudioGroup?.filter((item: { groupId: number | null; }) => item.groupId === selectedIvmGroupId)}
                            optionLabel="audioName"
                            optionValue="audioId"
                            display="comma"
                            disabled={!archiveTableToggle || statusDone || !editType}
                            placeholder="Виберіть аудіо:"
                            selectedItemsLabel={companiesData?.campaignACD?.ivmAudios?.length ? `Обрано: ${companiesData?.campaignACD?.ivmAudios?.length}` : ""}
                            maxSelectedLabels={0}
                        />
                    )}
                    <div className="col-12 md:col-12 pt-2 px-0 pb-0">
                        {
                            !!companiesData?.campaignACD?.ivmAudios?.length &&
                            <div className="users-list">
                                {audioItemsTable()}
                            </div>
                        }
                    </div>
                    {errorFields?.ivmAudios && <div className="col-12 md:col-12 px-0 pb-0 pt-0"><span
                        className="title p-error text-sm">{errorFields?.ivmAudios?.ivmAudios}</span></div>}
                </div>}

                <div className="p-fluid field col-12 md:col-6 mb-0"></div>
                {/*<Divider/>*/}
                {/*<div className="flex-column field col-12 md:col-4 mb-0">*/}
                {/*    <div>*/}
                {/*        <div className={classNames('mb-3', {'p-error': errorFields?.ivmListeningTime})}>Тривалість прослуховування (мін. 1 сек)*</div>*/}
                {/*    </div>*/}
                {/*    <InputNumber*/}
                {/*        min={0}*/}
                {/*        id="maxWaitTime"*/}
                {/*        value={companiesData?.campaignACD?.ivmListeningTime || null}*/}
                {/*        useGrouping={false}*/}
                {/*        className={classNames('max-w-20rem', {'p-invalid': errorFields?.ivmListeningTime})}*/}
                {/*        disabled={!archiveTableToggle || !editType}*/}
                {/*        onChange={(e) => {*/}
                {/*            dispatch(setCompanyField({section: 'campaignACD', fieldName: 'ivmListeningTime', value: e.value}));*/}
                {/*        }}*/}
                {/*        placeholder="0"*/}
                {/*    />*/}
                {/*    {errorFields?.ivmListeningTime && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
                {/*        className="title p-error text-sm">{errorFields?.ivmListeningTime?.ivmListeningTime}</span></div>}*/}
                {/*</div>*/}
            </div>
            {audioDialogVisible && (
                <AudioDialog skipBranchesFetch={companiesData?.permission?.branchIsBlock}/>
            )}
        </div>
    );
};

export default CampaignDialog_IVM_ACD;
